import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import { Grid, Input, NativeSelect, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import MaterialTable from 'material-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Pagination from 'react-js-pagination';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading';
import Header from '../../layout/Header';
import Subheader from '../../layout/Subheader';
import api from '../../services/api';
import { findPermission, TOKEN_PAGINATION } from '../../services/auth';
import '../Providers/pagination.style.css';
import useProvidersListStyles from '../Providers/style';
import useProviderStyles from '../Providers/Provider/style';

/**
 * Cadastro/edição de destino: evita o combo `rootForm` (57vh + !important) com scroll quebrado
 * e o `alignItems: center` do `content` que comprime a coluna de rolagem.
 */
const useDestinosFormLayout = makeStyles(() => ({
  formOuter: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    overflowX: 'hidden',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  formInner: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: '90%',
    marginTop: '31vh',
    marginBottom: '3%',
    overflowX: 'hidden',
  },
}));

/** Evita NaN (JSON vira null) e null/undefined — API não aceita null em enums. */
function toInt01(v, def = 0) {
  if (v === null || v === undefined || v === '') return def;
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

/**
 * ConsultarId serializa enums com JsonStringEnumConverter: "Sim" / "Nao" (e status "Ativo" / "Inativo").
 * Number("Nao") => NaN — sem isso todos os selects caem em 0 (Não).
 */
function simNaoTo01(v, def = 0) {
  if (v === null || v === undefined || v === '') return def;
  if (typeof v === 'number' && Number.isFinite(v)) return v ? 1 : 0;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const s = String(v).trim().toLowerCase();
  if (s === 'sim' || s === '1' || s === 'true') return 1;
  if (s === 'nao' || s === 'não' || s === '0' || s === 'false') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? (n ? 1 : 0) : def;
}

function statusApiTo01(v, def = 1) {
  if (v === null || v === undefined || v === '') return def;
  if (typeof v === 'number' && Number.isFinite(v)) return v ? 1 : 0;
  const s = String(v).trim().toLowerCase();
  if (s === 'ativo' || s === '1' || s === 'sim') return 1;
  if (s === 'inativo' || s === '0' || s === 'nao' || s === 'não') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? (n ? 1 : 0) : def;
}

/** Valores padrão para cadastro (API exige enums e textos com tamanho mínimo). */
const DEFAULT_CREATE = {
  destinoPagamento: '',
  razaoSocial: '',
  nomeFantasia: '',
  cnpj: '',
  nomePastaArquivos: '',
  status: 1,
  assinaturaClickSign: 0,
  clicksignPermiteRecusaAssinatura: 0,
  geraContratoMae: 0,
  geraContratoOperacao: 0,
  habilitaEnvioRemessa: 0,
  situacaoAprovacao: '',
  clicksignHoraInicialEnvioSegundaASexta: '',
  clicksignHoraFinalEnvioSegundaAQuinta: '',
  clicksignHoraFinalEnvioSexta: '',
  clicksignHoraAssinaturaSegundaAQuinta: '',
  clicksignHoraAssinaturaSexta: '',
};

function mapApiToForm(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return { ...DEFAULT_CREATE };
  const st = data.status ?? data.Status;
  const statusNum = statusApiTo01(st, 1);
  // API pode responder em camelCase ou PascalCase — normaliza para o formulário.
  return {
    ...DEFAULT_CREATE,
    destinoPagamentoId: data.destinoPagamentoId ?? data.DestinoPagamentoId,
    destinoPagamento: data.destinoPagamento ?? data.DestinoPagamento ?? '',
    razaoSocial: data.razaoSocial ?? data.RazaoSocial ?? '',
    nomeFantasia: data.nomeFantasia ?? data.NomeFantasia ?? '',
    cnpj: data.cnpj ?? data.Cnpj ?? '',
    nomePastaArquivos: data.nomePastaArquivos ?? data.NomePastaArquivos ?? '',
    status: statusNum,
    assinaturaClickSign: simNaoTo01(data.assinaturaClickSign ?? data.AssinaturaClickSign, 0),
    clicksignPermiteRecusaAssinatura: simNaoTo01(
      data.clicksignPermiteRecusaAssinatura ?? data.ClicksignPermiteRecusaAssinatura,
      0,
    ),
    geraContratoMae: simNaoTo01(data.geraContratoMae ?? data.GeraContratoMae, 0),
    geraContratoOperacao: simNaoTo01(data.geraContratoOperacao ?? data.GeraContratoOperacao, 0),
    habilitaEnvioRemessa: simNaoTo01(data.habilitaEnvioRemessa ?? data.HabilitaEnvioRemessa, 0),
    situacaoAprovacao: parseSituacaoAprovacao(data.situacaoAprovacao ?? data.SituacaoAprovacao),
    clicksignHoraInicialEnvioSegundaASexta:
      data.clicksignHoraInicialEnvioSegundaASexta ?? data.ClicksignHoraInicialEnvioSegundaASexta ?? '',
    clicksignHoraFinalEnvioSegundaAQuinta:
      data.clicksignHoraFinalEnvioSegundaAQuinta ?? data.ClicksignHoraFinalEnvioSegundaAQuinta ?? '',
    clicksignHoraFinalEnvioSexta: data.clicksignHoraFinalEnvioSexta ?? data.ClicksignHoraFinalEnvioSexta ?? '',
    clicksignHoraAssinaturaSegundaAQuinta:
      data.clicksignHoraAssinaturaSegundaAQuinta ?? data.ClicksignHoraAssinaturaSegundaAQuinta ?? '',
    clicksignHoraAssinaturaSexta: data.clicksignHoraAssinaturaSexta ?? data.ClicksignHoraAssinaturaSexta ?? '',
    dataAtualizacao: data.dataAtualizacao ?? data.DataAtualizacao,
    dataRegistro: data.dataRegistro ?? data.DataRegistro,
  };
}

function buildPayload(formValues, destinoPagamentoId, isEdit) {
  const id = Number(
    destinoPagamentoId ||
      formValues.destinoPagamentoId ||
      formValues.DestinoPagamentoId ||
      0,
  );
  const statusNum =
    typeof formValues.status === 'string'
      ? formValues.status === 'Ativo'
        ? 1
        : 0
      : Number(formValues.status);

  return {
    destinoPagamentoId: id,
    destinoPagamento: formValues.destinoPagamento ?? '',
    razaoSocial: formValues.razaoSocial ?? '',
    nomeFantasia: formValues.nomeFantasia ?? '',
    cnpj: formValues.cnpj ?? '',
    nomePastaArquivos: formValues.nomePastaArquivos ?? '',
    status: Number.isFinite(statusNum) ? statusNum : 1,
    assinaturaClickSign: toInt01(formValues.assinaturaClickSign, 0),
    clicksignPermiteRecusaAssinatura: toInt01(formValues.clicksignPermiteRecusaAssinatura, 0),
    geraContratoMae: toInt01(formValues.geraContratoMae, 0),
    geraContratoOperacao: toInt01(formValues.geraContratoOperacao, 0),
    habilitaEnvioRemessa: toInt01(formValues.habilitaEnvioRemessa, 0),
    situacaoAprovacao:
      formValues.situacaoAprovacao === '' ||
      formValues.situacaoAprovacao === null ||
      formValues.situacaoAprovacao === undefined
        ? null
        : Number(formValues.situacaoAprovacao),
    clicksignHoraInicialEnvioSegundaASexta: formValues.clicksignHoraInicialEnvioSegundaASexta ?? '',
    clicksignHoraFinalEnvioSegundaAQuinta: formValues.clicksignHoraFinalEnvioSegundaAQuinta ?? '',
    clicksignHoraFinalEnvioSexta: formValues.clicksignHoraFinalEnvioSexta ?? '',
    clicksignHoraAssinaturaSegundaAQuinta: formValues.clicksignHoraAssinaturaSegundaAQuinta ?? '',
    clicksignHoraAssinaturaSexta: formValues.clicksignHoraAssinaturaSexta ?? '',
    ...(isEdit && (formValues.dataAtualizacao || formValues.DataAtualizacao)
      ? { dataAtualizacao: formValues.dataAtualizacao ?? formValues.DataAtualizacao }
      : {}),
  };
}

function statusLabel(v) {
  if (v === 1 || v === '1' || v === 'Ativo') return 'Ativo';
  return 'Inativo';
}

// Subconjunto de enumAntecipacoesSituacao relevante para o passo em que a
// antecipação muda para Aprovada neste destino. Os passos 5/6 cobrem destinos
// sem assinatura; 12/13 cobrem o fluxo Clicksign.
const SITUACAO_APROVACAO_OPCOES = [
  { value: 5, label: '5 — Pendente enviada ao fornecedor' },
  { value: 6, label: '6 — Aprovada enviada para destino' },
  { value: 12, label: '12 — Passo 4 — Contrato aguardando assinatura' },
  { value: 13, label: '13 — Passo 5 — Contrato assinado aguardando pagamento' },
];

// Backend usa System.Text.Json com JsonStringEnumConverter, então enums voltam
// como string (ex: "EnviadaFornecedor") em vez de int. Mapa para converter
// de volta no mapApiToForm. Cobre só os valores expostos no select.
const SITUACAO_APROVACAO_NOME_PARA_VALOR = {
  EnviadaFornecedor: 5,
  EnviadaDestinoPagamento: 6,
  EnviadoContratoParaFornecedorAguardandoAssinar: 12,
  ContratoAssinadoPeloFornecedor: 13,
};

function parseSituacaoAprovacao(raw) {
  if (raw === null || raw === undefined || raw === '') return '';
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    if (SITUACAO_APROVACAO_NOME_PARA_VALOR[raw] !== undefined) {
      return SITUACAO_APROVACAO_NOME_PARA_VALOR[raw];
    }
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return '';
}

const CM_HTML_PLACEHOLDER =
  '<p>Instrumento de cessão — placeholders: {{CedenteRazaoSocial}}, {{CedenteCnpj}}, {{CedenteEndereco}}, {{CedenteCidade}}, {{CedenteUF}}, {{DataContrato}}</p>';

function emptyContratoMaster(destinoPagamentoId) {
  return {
    contratoMasterId: 0,
    destinoPagamentoId: Number(destinoPagamentoId) || 0,
    nomeContrato: '',
    conteudoHtml: CM_HTML_PLACEHOLDER,
    status: 1,
    dataRegistro: null,
    dataAtualizacao: null,
  };
}

/** API pode retornar camelCase ou PascalCase. */
function mapCmRow(r) {
  if (!r) return null;
  const st = r.status ?? r.Status;
  const statusNum = st === 0 || st === '0' || st === 'Inativo' ? 0 : 1;
  return {
    contratoMasterId: Number(r.contratoMasterId ?? r.ContratoMasterId ?? 0),
    destinoPagamentoId: Number(r.destinoPagamentoId ?? r.DestinoPagamentoId ?? 0),
    nomeContrato: r.nomeContrato ?? r.NomeContrato ?? '',
    conteudoHtml: r.conteudoHtml ?? r.ConteudoHtml ?? '',
    status: statusNum,
    dataRegistro: r.dataRegistro ?? r.DataRegistro ?? null,
    dataAtualizacao: r.dataAtualizacao ?? r.DataAtualizacao ?? null,
  };
}

function formatDateTimeBr(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString('pt-BR');
  } catch (e) {
    return '—';
  }
}

/** Lista da API (camelCase ou PascalCase) para o grid. */
function mapListItem(i) {
  if (!i || typeof i !== 'object') return i;
  const st = i.status ?? i.Status;
  return {
    ...i,
    destinoPagamentoId: i.destinoPagamentoId ?? i.DestinoPagamentoId,
    destinoPagamento: i.destinoPagamento ?? i.DestinoPagamento ?? '',
    razaoSocial: i.razaoSocial ?? i.RazaoSocial ?? '',
    nomeFantasia: i.nomeFantasia ?? i.NomeFantasia ?? '',
    status: statusApiTo01(st, 1),
  };
}

/** Módulo Destinos de Pagamentos (API `enumModulos.DestinosPagamentos`). */
const MODULO_DESTINOS_PAGAMENTOS = 1880;

export function DestinosPagamentos() {
  const classes = useProvidersListStyles();
  /** Mesmos acordeões, campos (Paper+Input) e colunas que a tela de fornecedor. */
  const pv = useProviderStyles();
  const formLayout = useDestinosFormLayout();
  const history = useHistory();
  const { id: routeId } = useParams();

  const [isLoading, setLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [formValues, setFormValues] = useState(DEFAULT_CREATE);
  const [contratosMaster, setContratosMaster] = useState([]);
  const [cmDraft, setCmDraft] = useState(() => emptyContratoMaster(0));
  const [cmSaving, setCmSaving] = useState(false);
  const [cmSelectKey, setCmSelectKey] = useState('__new__');

  // Bancos remessas habilitados (relacionamento N:N — Épico 3).
  const [bancosRemessasDisponiveis, setBancosRemessasDisponiveis] = useState([]);
  const [bancosRemessasSelecionados, setBancosRemessasSelecionados] = useState([]);
  const [brLoading, setBrLoading] = useState(false);
  const [brSaving, setBrSaving] = useState(false);

  const [razaoSocialInput, setRazaoSocialInput] = useState('');
  const [nomeFantasiaInput, setNomeFantasiaInput] = useState('');
  const [destinoPagamentoInput, setDestinoPagamentoInput] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('ativo');
  const [currentPage, setCurrentPage] = useState(1);
  const [itensPerPage, setItensPerPage] = useState(50);
  const [pagination, setPagination] = useState({ qtdTotalRegistros: 0 });

  const id = routeId;
  const pathnameForm = history.location.pathname;
  const isCreateRoute = pathnameForm.includes('/cadastrar');
  const isEditRoute = pathnameForm.includes('/editar');
  /** Somente `/destinos-pagamentos/:id` — leitura, como visualizar fornecedor. */
  const isViewRoute = Boolean(id) && !isCreateRoute && !isEditRoute;
  const isFormMode = Boolean(id) || isCreateRoute;
  /** Pode alterar campos e gravar: cadastro novo ou rota `/.../editar`. */
  const isEdicaoPermitida = isCreateRoute || isEditRoute;

  /** Grid com paginação e filtros no servidor (mesmo padrão da lista de Fornecedores). */
  const fetchDestinos = useCallback(
    async (pageNumber, statusAtual = statusFiltro) => {
      const page = pageNumber != null && pageNumber !== '' ? Number(pageNumber) : 1;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('paginaInicial', String(Number.isFinite(page) && page > 0 ? page : 1));
        params.set('itensPorPagina', String(itensPerPage));
        if (razaoSocialInput.trim()) params.set('razaoSocial', razaoSocialInput.trim());
        if (nomeFantasiaInput.trim()) params.set('nomeFantasia', nomeFantasiaInput.trim());
        if (destinoPagamentoInput.trim()) params.set('destinoPagamento', destinoPagamentoInput.trim());
        if (statusAtual === 'ativo') params.set('status', 'Ativo');
        else if (statusAtual === 'inativo') params.set('status', 'Inativo');

        const response = await api.get(`DestinosPagamentos/Consultar?${params.toString()}`);

        if (response?.data?.status === 'Sucesso') {
          setPagination(response.data.paginacao || { qtdTotalRegistros: 0 });
          const raw = Array.isArray(response.data.resposta) ? response.data.resposta : [];
          setItems(raw.map(mapListItem));
        } else {
          setItems([]);
          setPagination({ qtdTotalRegistros: 0 });
        }
      } catch (error) {
        setItems([]);
        setPagination({ qtdTotalRegistros: 0 });
        toast.error('Não foi possível carregar os destinos de pagamento.');
      } finally {
        setLoading(false);
      }
    },
    [itensPerPage, razaoSocialInput, nomeFantasiaInput, destinoPagamentoInput, statusFiltro],
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchDestinos(pageNumber, statusFiltro);
  };

  const fetchById = useCallback(async (destinoPagamentoId) => {
    if (!destinoPagamentoId) return;
    setLoading(true);
    try {
      const response = await api.get(`DestinosPagamentos/ConsultarId?id=${destinoPagamentoId}`);
      if (response?.data?.status === 'Sucesso' && response?.data?.resposta) {
        setFormValues(mapApiToForm(response.data.resposta));
      } else {
        setFormValues({ ...DEFAULT_CREATE });
      }
    } catch (error) {
      toast.error('Não foi possível carregar o destino de pagamento.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContratosMaster = useCallback(async (destinoPagamentoId) => {
    if (!destinoPagamentoId) return;
    try {
      const response = await api.get(
        `ContratosMasters/ConsultarPorDestinoPagamentoId?destinoPagamentoId=${destinoPagamentoId}`,
      );
      if (response?.data?.status === 'Sucesso') {
        const raw = Array.isArray(response.data.resposta) ? response.data.resposta : [];
        const rows = raw.map((x) => mapCmRow(x)).filter(Boolean);
        setContratosMaster(rows);
        const ativo = rows.find((r) => r.status === 1);
        if (ativo) {
          setCmSelectKey(String(ativo.contratoMasterId));
          setCmDraft({
            ...ativo,
            conteudoHtml: ativo.conteudoHtml || CM_HTML_PLACEHOLDER,
          });
        } else {
          setCmSelectKey('__new__');
          setCmDraft(emptyContratoMaster(destinoPagamentoId));
        }
      } else {
        setContratosMaster([]);
        setCmSelectKey('__new__');
        setCmDraft(emptyContratoMaster(destinoPagamentoId));
      }
    } catch (e) {
      setContratosMaster([]);
      setCmSelectKey('__new__');
      setCmDraft(emptyContratoMaster(destinoPagamentoId));
    }
  }, []);

  useEffect(() => {
    if (id && !history.location.pathname.includes('/cadastrar')) {
      fetchContratosMaster(id);
    } else {
      setContratosMaster([]);
      setCmSelectKey('__new__');
      setCmDraft(emptyContratoMaster(0));
    }
  }, [id, history.location.pathname, fetchContratosMaster]);

  const fetchBancosRemessas = useCallback(async (destinoPagamentoId) => {
    if (!destinoPagamentoId) return;
    setBrLoading(true);
    try {
      const [resAtivos, resHabilitados] = await Promise.all([
        api.get('BancosRemessas/ConsultarAtivos'),
        api.get(`DestinosPagamentos/ConsultarBancosRemessas?destinoPagamentoId=${destinoPagamentoId}`),
      ]);

      const ativosRaw = Array.isArray(resAtivos?.data?.resposta) ? resAtivos.data.resposta : [];
      const ativos = ativosRaw.map((b) => ({
        bancoRemessaId: b.bancoRemessaId ?? b.BancoRemessaId,
        bancoId: b.bancoId ?? b.BancoId,
        nome: b.nome ?? b.Nome ?? '',
        agencia: b.agencia ?? b.Agencia ?? '',
        conta: b.conta ?? b.Conta ?? '',
        dac: b.dac ?? b.Dac ?? '',
        tipoPagamento: b.tipoPagamento ?? b.TipoPagamento,
      }));
      setBancosRemessasDisponiveis(ativos);

      // Vínculos do destino podem incluir bancos que foram inativados depois.
      // Filtra pra manter só os que ainda estão ativos — evita contagem inconsistente
      // ("3 de 2 selecionados") e impede que o save mantenha vínculo fantasma.
      const idsAtivos = new Set(ativos.map((b) => b.bancoRemessaId));
      const habilitadosRaw = Array.isArray(resHabilitados?.data?.resposta) ? resHabilitados.data.resposta : [];
      const idsHabilitados = habilitadosRaw
        .map((h) => h.bancoRemessaId ?? h.BancoRemessaId)
        .filter((v) => typeof v === 'number' && idsAtivos.has(v));
      setBancosRemessasSelecionados(idsHabilitados);
    } catch (e) {
      setBancosRemessasDisponiveis([]);
      setBancosRemessasSelecionados([]);
    } finally {
      setBrLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id && !history.location.pathname.includes('/cadastrar')) {
      fetchBancosRemessas(id);
    } else {
      setBancosRemessasDisponiveis([]);
      setBancosRemessasSelecionados([]);
    }
  }, [id, history.location.pathname, fetchBancosRemessas]);

  const toggleBancoRemessa = (bancoRemessaId) => {
    setBancosRemessasSelecionados((prev) =>
      prev.includes(bancoRemessaId)
        ? prev.filter((x) => x !== bancoRemessaId)
        : [...prev, bancoRemessaId],
    );
  };

  const salvarConfiguracoesRemessa = async () => {
    if (!id) return;
    const idsSubmetidos = [...bancosRemessasSelecionados];
    setBrSaving(true);
    try {
      // 1) PUT no destino completo — persiste habilitaEnvioRemessa + situacaoAprovacao
      //    (campos que estão no acordeão mas só vão pra base via endpoint principal).
      const payload = buildPayload(formValues, id, true);
      const resDestino = await api.put('DestinosPagamentos', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (resDestino?.data?.status === 'Inconsistencia') {
        const erroDestino = resDestino?.data?.resposta?.[0]?.message;
        toast.error(erroDestino || 'Não foi possível salvar configurações do destino.');
        return;
      }
      // Sincroniza dataAtualizacao retornado para evitar conflito otimista no próximo save.
      const novoFormValues = mapApiToForm(resDestino?.data?.resposta || {});
      setFormValues((prev) => ({ ...prev, dataAtualizacao: novoFormValues.dataAtualizacao ?? prev.dataAtualizacao }));

      // 2) Sincroniza N:N de bancos remessa
      const res = await api.post('DestinosPagamentos/SalvarBancosRemessas', {
        destinoPagamentoId: Number(id),
        bancosRemessasIds: idsSubmetidos,
      });
      if (res?.data?.status === 'Sucesso') {
        toast.success('Configurações de remessa atualizadas.');
        const respLista = Array.isArray(res?.data?.resposta) ? res.data.resposta : [];
        const idsRetornados = respLista
          .map((h) => h.bancoRemessaId ?? h.BancoRemessaId)
          .filter((v) => typeof v === 'number');
        if (idsRetornados.length > 0 || idsSubmetidos.length === 0) {
          setBancosRemessasSelecionados(idsRetornados);
        } else {
          setBancosRemessasSelecionados(idsSubmetidos);
        }
      } else {
        const erros = res?.data?.resposta || [];
        if (erros.length > 0) {
          erros.forEach((e) => toast.error(e.message));
        } else {
          toast.error('Erro ao salvar bancos remessas.');
        }
      }
    } catch (e) {
      const msg = e?.response?.data?.resposta?.[0]?.message;
      toast.error(msg || 'Erro ao salvar configurações de remessa.');
    } finally {
      setBrSaving(false);
    }
  };

  useEffect(() => {
    if (isFormMode) {
      if (id && !history.location.pathname.includes('/cadastrar')) {
        fetchById(id);
      } else {
        setFormValues({ ...DEFAULT_CREATE });
        setLoading(false);
      }
    }
  }, [id, isFormMode, fetchById, history.location.pathname]);

  useEffect(() => {
    if (isFormMode) return;
    try {
      const stored = sessionStorage.getItem(TOKEN_PAGINATION);
      if (stored) {
        const userPage = JSON.parse(stored).paginacao.itensPorPagina;
        setItensPerPage(userPage === 0 ? 50 : userPage);
      }
    } catch (e) {
      setItensPerPage(50);
    }
  }, [isFormMode]);

  useEffect(() => {
    if (isFormMode) return;
    setCurrentPage(1);
    const delayDebounceFn = setTimeout(() => {
      fetchDestinos(1, statusFiltro);
    }, 1500);
    return () => clearTimeout(delayDebounceFn);
  }, [isFormMode, razaoSocialInput, nomeFantasiaInput, destinoPagamentoInput, itensPerPage, fetchDestinos]);

  const salvarContratoMaster = async () => {
    if (isViewRoute) {
      return;
    }
    if (!id) {
      toast.warn('Salve o destino de pagamento antes de cadastrar o contrato master.');
      return;
    }
    setCmSaving(true);
    try {
      const destinoPagamentoId = Number(id);
      const isNew = !(Number(cmDraft.contratoMasterId) > 0);
      const body = isNew
        ? {
            destinoPagamentoId,
            nomeContrato: cmDraft.nomeContrato,
            conteudoHtml: cmDraft.conteudoHtml,
            status: Number(cmDraft.status),
          }
        : {
            contratoMasterId: cmDraft.contratoMasterId,
            destinoPagamentoId,
            nomeContrato: cmDraft.nomeContrato,
            conteudoHtml: cmDraft.conteudoHtml,
            status: Number(cmDraft.status),
            dataRegistro: cmDraft.dataRegistro,
            dataAtualizacao: cmDraft.dataAtualizacao,
          };
      const response = isNew
        ? await api.post('ContratosMasters', body)
        : await api.put('ContratosMasters', body);
      if (response?.data?.status === 'Inconsistencia') {
        toast.error('Não foi possível salvar o contrato master.');
        return;
      }
      toast.success(isNew ? 'Contrato master criado.' : 'Contrato master atualizado.');
      const resposta = mapCmRow(response?.data?.resposta);
      await fetchContratosMaster(id);
      if (resposta?.contratoMasterId) {
        setCmSelectKey(String(resposta.contratoMasterId));
        setCmDraft({
          ...resposta,
          conteudoHtml: resposta.conteudoHtml || CM_HTML_PLACEHOLDER,
        });
      }
    } catch (e) {
      toast.error('Não foi possível salvar o contrato master.');
    } finally {
      setCmSaving(false);
    }
  };

  const onSubmit = async () => {
    if (!isEdicaoPermitida) return;
    setSaving(true);
    try {
      const payload = buildPayload(formValues, id, Boolean(id));

      const response = id
        ? await api.put('DestinosPagamentos', payload, {
            headers: { 'Content-Type': 'application/json' },
          })
        : await api.post('DestinosPagamentos', payload, {
            headers: { 'Content-Type': 'application/json' },
          });

      if (response?.data?.status === 'Inconsistencia') {
        toast.error('Não foi possível salvar o destino de pagamento.');
        return;
      }

      toast.success(id ? 'Destino de pagamento alterado com sucesso!' : 'Destino de pagamento criado com sucesso!');

      if (id) {
        await fetchById(id);
        await fetchContratosMaster(id);
      } else {
        const novoId =
          response?.data?.resposta?.destinoPagamentoId ?? response?.data?.resposta?.DestinoPagamentoId;
        if (novoId) {
          history.push(`/app/destinos-pagamentos/${novoId}/editar`);
        } else {
          history.push('/app/destinos-pagamentos');
        }
      }
    } catch (error) {
      toast.error('Não foi possível salvar o destino de pagamento.');
    } finally {
      setSaving(false);
    }
  };

  const memoizedTable = useMemo(
    () => (
      <MaterialTable
        className={classes.table}
        title="Multiple Actions Preview"
        columns={[
          {
            title: 'ID',
            field: 'destinoPagamentoId',
            cellStyle: {
              fontSize: '13px',
              padding: '8px 10px',
              width: '20px',
            },
          },
          {
            title: 'Razão Social',
            field: 'razaoSocial',
            cellStyle: {
              fontSize: '13px',
              padding: '8px 10px',
              width: '250px',
            },
          },
          {
            title: 'Nome Fantasia',
            field: 'nomeFantasia',
            cellStyle: {
              fontSize: '13px',
              padding: '8px 10px',
              width: '200px',
            },
          },
          {
            title: 'Destino de Pagamento',
            field: 'destinoPagamento',
            cellStyle: {
              fontSize: '13px',
              padding: '8px 10px',
            },
          },
          {
            title: 'Status',
            field: 'status',
            render: (row) => statusLabel(row.status),
            cellStyle: {
              fontSize: '13px',
              padding: '8px 16px',
              width: '80px',
            },
          },
          {
            title: 'Ações',
            field: 'actions',
            headerStyle: {
              textAlign: 'center',
            },
            cellStyle: {
              fontSize: '13px',
              textAlign: 'center',
              padding: '8px 16px',
            },
            filtering: false,
            sorting: false,
            disableClick: true,
            render: (rowData) => (
              <>
                {!!findPermission(MODULO_DESTINOS_PAGAMENTOS, 'Consultar') && (
                  <Link
                    className={classes.buttonVisualize}
                    to={`/app/destinos-pagamentos/${rowData.destinoPagamentoId}`}
                  >
                    <VisibilityIcon style={{ fontSize: 30, color: '#4AAF05' }} />
                  </Link>
                )}
                {!!findPermission(MODULO_DESTINOS_PAGAMENTOS, 'Alterar') && (
                  <Link
                    className={classes.buttonEdit}
                    to={`/app/destinos-pagamentos/${rowData.destinoPagamentoId}/editar`}
                  >
                    <EditIcon style={{ fontSize: 30, color: '#FB8832' }} />
                  </Link>
                )}
              </>
            ),
          },
        ]}
        data={items}
        onRowClick={(event, rowData) => {
          if (!!findPermission(MODULO_DESTINOS_PAGAMENTOS, 'Consultar')) {
            window.location.href =
              window.location.origin + `/app/destinos-pagamentos/${rowData.destinoPagamentoId}`;
          }
        }}
        options={{
          showTitle: false,
          search: false,
          toolbar: false,
          headerStyle: {
            backgroundColor: '#F5F5F5',
            textTransform: 'none',
            fontSize: '15px',
          },
          filtering: true,
          paging: false,
          rowStyle: {
            fontSize: '14px',
          },
        }}
      />
    ),
    [items, classes.table, classes.buttonVisualize, classes.buttonEdit],
  );

  const statusSelectValue = formValues.status === 0 || formValues.status === '0' ? 0 : 1;
  /** Visualização (`/id` sem `/editar`): mesma ideia do fornecedor em modo consulta. */
  const somenteLeitura = !isEdicaoPermitida;

  return (
    <div className={classes.root}>
      <Header />
      <Subheader
        title={
          isFormMode
            ? isCreateRoute
              ? 'Cadastrar destino de pagamentos'
              : isEditRoute
                ? `Atualizar destino de pagamentos '${formValues.destinoPagamento || ''}'`
                : `Visualizando destino de pagamentos — ${formValues.destinoPagamento || ''}`
            : 'Destinos de pagamentos'
        }
        back={isFormMode ? '/app/destinos-pagamentos' : '/app/cadastros'}
        loading={isLoading || isSaving}
        submit={isFormMode && isEdicaoPermitida ? onSubmit : undefined}
        submitText={
          isFormMode && isEdicaoPermitida ? (id ? 'Atualizar' : 'Cadastrar') : undefined
        }
        extraBtn={!isFormMode}
        extraBtnText="Novo item"
        extraBtnFunction={() => history.push('/app/destinos-pagamentos/cadastrar')}
      />

      {!isFormMode ? (
        <div className={classes.content}>
          <div className={classes.rootForm}>
            <header>
              <Grid container className={classes.searchContainer}>
                <Grid item xs={2} className={classes.item}>
                  <label className={classes.label}>Razão Social</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      value={razaoSocialInput}
                      placeholder="Razão Social"
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      onChange={(e) =>
                        setRazaoSocialInput(
                          e.target.value.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
                        )
                      }
                    />
                  </Paper>
                </Grid>
                <Grid item xs={2} className={classes.item}>
                  <label className={classes.label}>Nome Fantasia</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      value={nomeFantasiaInput}
                      placeholder="Nome Fantasia"
                      onChange={(e) => setNomeFantasiaInput(e.target.value)}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={2} className={classes.item}>
                  <label className={classes.label}>Destino de Pagamento</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      value={destinoPagamentoInput}
                      placeholder="Destino"
                      onChange={(e) => setDestinoPagamentoInput(e.target.value)}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={2} className={classes.item}>
                  <label className={classes.label}>Status</label>
                  <Paper className={classes.medium} elevation={0}>
                    <NativeSelect
                      className={classes.input}
                      value={statusFiltro}
                      onChange={(e) => {
                        const v = e.target.value;
                        setStatusFiltro(v);
                        setCurrentPage(1);
                        fetchDestinos(1, v);
                      }}
                    >
                      <option value="">Todos</option>
                      <option value="ativo">Ativo</option>
                      <option value="inativo">Inativo</option>
                    </NativeSelect>
                  </Paper>
                </Grid>
                <Grid item xs={2} />
                <Grid
                  item
                  xs={2}
                  className={classes.item}
                  style={{ margin: '0 0 0 auto', display: 'flex', justifyContent: 'flex-end' }}
                >
                  <button
                    type="button"
                    className={classes.buttonFilter}
                    onClick={() => fetchDestinos(currentPage, statusFiltro)}
                  >
                    Aplicar Filtro
                  </button>
                </Grid>
              </Grid>
            </header>
            {isLoading ? (
              <Loading />
            ) : (
              <>
                {memoizedTable}
                <div className="footer-table">
                  <Pagination
                    activePage={currentPage}
                    itemsCountPerPage={itensPerPage}
                    totalItemsCount={pagination.qtdTotalRegistros || 0}
                    pageRangeDisplayed={5}
                    onChange={handlePageChange}
                  />
                  <div className="select-page">
                    <label>Qtd. por página</label>
                    <NativeSelect
                      onChange={(e) => setItensPerPage(Number(e.currentTarget.value))}
                      value={itensPerPage}
                    >
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                    </NativeSelect>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className={formLayout.formOuter}>
          <div className={formLayout.formInner}>
          {isLoading ? (
            <Loading />
          ) : (
          <div>
            {isCreateRoute ? (
              <Grid container>
                <Grid item xs={6}>
                  <label className={pv.label}>Destino de Pagamento</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.destinoPagamento || ''}
                      onChange={(e) => setFormValues({ ...formValues, destinoPagamento: e.target.value })}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Status</label>
                  <Paper className={pv.medium} elevation={0}>
                    <NativeSelect
                      className={pv.input}
                      value={statusSelectValue}
                      onChange={(e) =>
                        setFormValues({ ...formValues, status: Number(e.target.value) })
                      }
                    >
                      <option value={1}>Ativo</option>
                      <option value={0}>Inativo</option>
                    </NativeSelect>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Razão social</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.razaoSocial || ''}
                      onChange={(e) => setFormValues({ ...formValues, razaoSocial: e.target.value })}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Nome fantasia</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.nomeFantasia || ''}
                      onChange={(e) => setFormValues({ ...formValues, nomeFantasia: e.target.value })}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>CNPJ</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.cnpj || ''}
                      onChange={(e) => setFormValues({ ...formValues, cnpj: e.target.value })}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Nome pasta arquivos</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.nomePastaArquivos || ''}
                      onChange={(e) => setFormValues({ ...formValues, nomePastaArquivos: e.target.value })}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Gera contrato mãe</label>
                  <Paper className={pv.medium} elevation={0}>
                    <NativeSelect
                      className={pv.input}
                      value={Number(formValues.geraContratoMae ?? 0)}
                      onChange={(e) =>
                        setFormValues({ ...formValues, geraContratoMae: Number(e.target.value) })
                      }
                    >
                      <option value={0}>Não</option>
                      <option value={1}>Sim</option>
                    </NativeSelect>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Gera contrato operação</label>
                  <Paper className={pv.medium} elevation={0}>
                    <NativeSelect
                      className={pv.input}
                      value={Number(formValues.geraContratoOperacao ?? 0)}
                      onChange={(e) =>
                        setFormValues({ ...formValues, geraContratoOperacao: Number(e.target.value) })
                      }
                    >
                      <option value={0}>Não</option>
                      <option value={1}>Sim</option>
                    </NativeSelect>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Habilita envio de remessa</label>
                  <Paper className={pv.medium} elevation={0}>
                    <NativeSelect
                      className={pv.input}
                      value={Number(formValues.habilitaEnvioRemessa ?? 0)}
                      onChange={(e) =>
                        setFormValues({ ...formValues, habilitaEnvioRemessa: Number(e.target.value) })
                      }
                    >
                      <option value={0}>Não</option>
                      <option value={1}>Sim</option>
                    </NativeSelect>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Situação de aprovação</label>
                  <Paper className={pv.medium} elevation={0}>
                    <NativeSelect
                      className={pv.input}
                      value={formValues.situacaoAprovacao ?? ''}
                      onChange={(e) =>
                        setFormValues({
                          ...formValues,
                          situacaoAprovacao: e.target.value === '' ? '' : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">Não definida</option>
                      {SITUACAO_APROVACAO_OPCOES.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </NativeSelect>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Assinatura Clicksign</label>
                  <Paper className={pv.medium} elevation={0}>
                    <NativeSelect
                      className={pv.input}
                      value={Number(formValues.assinaturaClickSign ?? 0)}
                      onChange={(e) =>
                        setFormValues({ ...formValues, assinaturaClickSign: Number(e.target.value) })
                      }
                    >
                      <option value={0}>Não</option>
                      <option value={1}>Sim</option>
                    </NativeSelect>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Clicksign permite recusa</label>
                  <Paper className={pv.medium} elevation={0}>
                    <NativeSelect
                      className={pv.input}
                      value={Number(formValues.clicksignPermiteRecusaAssinatura ?? 0)}
                      onChange={(e) =>
                        setFormValues({
                          ...formValues,
                          clicksignPermiteRecusaAssinatura: Number(e.target.value),
                        })
                      }
                    >
                      <option value={0}>Não</option>
                      <option value={1}>Sim</option>
                    </NativeSelect>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Hora inicial envio (seg-sex) HH:mm</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.clicksignHoraInicialEnvioSegundaASexta || ''}
                      onChange={(e) =>
                        setFormValues({
                          ...formValues,
                          clicksignHoraInicialEnvioSegundaASexta: e.target.value,
                        })
                      }
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Hora final envio (seg-qui) HH:mm</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.clicksignHoraFinalEnvioSegundaAQuinta || ''}
                      onChange={(e) =>
                        setFormValues({
                          ...formValues,
                          clicksignHoraFinalEnvioSegundaAQuinta: e.target.value,
                        })
                      }
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Hora final envio (sexta) HH:mm</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.clicksignHoraFinalEnvioSexta || ''}
                      onChange={(e) =>
                        setFormValues({ ...formValues, clicksignHoraFinalEnvioSexta: e.target.value })
                      }
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Hora assinatura (seg-qui) HH:mm</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.clicksignHoraAssinaturaSegundaAQuinta || ''}
                      onChange={(e) =>
                        setFormValues({
                          ...formValues,
                          clicksignHoraAssinaturaSegundaAQuinta: e.target.value,
                        })
                      }
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <label className={pv.label}>Hora assinatura (sexta) HH:mm</label>
                  <Paper className={pv.medium} elevation={0}>
                    <Input
                      className={pv.input}
                      value={formValues.clicksignHoraAssinaturaSexta || ''}
                      onChange={(e) =>
                        setFormValues({ ...formValues, clicksignHoraAssinaturaSexta: e.target.value })
                      }
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Após salvar, você poderá cadastrar o <strong>contrato master</strong> (HTML por
                    destino) na edição deste registro.
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <>
                <Accordion defaultExpanded className={pv.accordionRoot}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={pv.summary}>
                    <Typography className={pv.heading}>Dados do destino</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={pv.details}>
                    <Grid container>
                      <Grid item xs={6}>
                        <label className={pv.label}>Destino de Pagamento</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.destinoPagamento || ''}
                            onChange={(e) =>
                              setFormValues({ ...formValues, destinoPagamento: e.target.value })
                            }
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Status</label>
                        <Paper className={pv.medium} elevation={0}>
                          <NativeSelect
                            className={pv.input}
                            disabled={somenteLeitura}
                            value={statusSelectValue}
                            onChange={(e) =>
                              setFormValues({ ...formValues, status: Number(e.target.value) })
                            }
                          >
                            <option value={1}>Ativo</option>
                            <option value={0}>Inativo</option>
                          </NativeSelect>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Razão social</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.razaoSocial || ''}
                            onChange={(e) =>
                              setFormValues({ ...formValues, razaoSocial: e.target.value })
                            }
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Nome fantasia</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.nomeFantasia || ''}
                            onChange={(e) =>
                              setFormValues({ ...formValues, nomeFantasia: e.target.value })
                            }
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>CNPJ</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.cnpj || ''}
                            onChange={(e) =>
                              setFormValues({ ...formValues, cnpj: e.target.value })
                            }
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Nome pasta arquivos</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.nomePastaArquivos || ''}
                            onChange={(e) =>
                              setFormValues({ ...formValues, nomePastaArquivos: e.target.value })
                            }
                          />
                        </Paper>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded className={pv.accordionRoot}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={pv.summary}>
                    <Typography className={pv.heading}>Contrato Master</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={pv.details}>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Por padrão é exibido o contrato master <strong>ativo</strong> deste destino. Se não
                      houver nenhum ativo, o formulário fica pronto para <strong>cadastrar um novo</strong>.
                      Você pode trocar no seletor para editar outra versão (inativa) ou criar outro modelo.
                    </Typography>
                    <Grid container>
                      <Grid item xs={12}>
                        <label className={pv.label}>Contrato</label>
                        <Paper className={pv.medium} elevation={0} style={{ width: '99%' }}>
                          <NativeSelect
                            className={pv.input}
                            disabled={somenteLeitura}
                            value={cmSelectKey}
                            onChange={(e) => {
                              const v = e.target.value;
                              setCmSelectKey(v);
                              if (v === '__new__') {
                                setCmDraft(emptyContratoMaster(id));
                                return;
                              }
                              const found = contratosMaster.find(
                                (r) => String(r.contratoMasterId) === String(v),
                              );
                              if (found) {
                                setCmDraft({
                                  ...found,
                                  conteudoHtml: found.conteudoHtml || CM_HTML_PLACEHOLDER,
                                });
                              }
                            }}
                          >
                            <option value="__new__">— Cadastrar novo contrato master —</option>
                            {contratosMaster.map((r) => (
                              <option key={r.contratoMasterId} value={String(r.contratoMasterId)}>
                                #{r.contratoMasterId} — {r.nomeContrato || '(sem nome)'} —{' '}
                                {r.status === 1 ? 'Ativo' : 'Inativo'}
                              </option>
                            ))}
                          </NativeSelect>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Status do contrato master</label>
                        <Paper className={pv.medium} elevation={0}>
                          <NativeSelect
                            className={pv.input}
                            disabled={somenteLeitura}
                            value={cmDraft.status === 0 ? 0 : 1}
                            onChange={(e) =>
                              setCmDraft({ ...cmDraft, status: Number(e.target.value) })
                            }
                          >
                            <option value={1}>Ativo</option>
                            <option value={0}>Inativo</option>
                          </NativeSelect>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <label className={pv.label}>Nome do contrato</label>
                        <Paper className={pv.medium} elevation={0} style={{ width: '99%' }}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={cmDraft.nomeContrato || ''}
                            onChange={(e) => setCmDraft({ ...cmDraft, nomeContrato: e.target.value })}
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <label className={pv.label}>Conteúdo HTML</label>
                        <Paper className={pv.medium} elevation={0} style={{ width: '99%' }}>
                          <textarea
                            className={pv.input}
                            value={cmDraft.conteudoHtml || ''}
                            disabled={somenteLeitura}
                            onChange={(e) =>
                              setCmDraft({ ...cmDraft, conteudoHtml: e.target.value })
                            }
                            style={{
                              textTransform: 'none',
                              width: '100%',
                              boxSizing: 'border-box',
                              height: 220,
                              resize: 'vertical',
                              overflowY: 'auto',
                            }}
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <div className={pv.registerBtn}>
                          <button
                            type="button"
                            className={pv.button}
                            disabled={cmSaving || somenteLeitura}
                            onClick={salvarContratoMaster}
                          >
                            {cmSaving ? 'Salvando…' : 'Salvar contrato master'}
                          </button>
                        </div>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded className={pv.accordionRoot}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={pv.summary}>
                    <Typography className={pv.heading}>Contrato antecipação / ClickSign</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={pv.details}>
                    <Grid container>
                      <Grid item xs={6}>
                        <label className={pv.label}>Gera contrato mãe</label>
                        <Paper className={pv.medium} elevation={0}>
                          <NativeSelect
                            className={pv.input}
                            disabled={somenteLeitura}
                            value={Number(formValues.geraContratoMae ?? 0)}
                            onChange={(e) =>
                              setFormValues({ ...formValues, geraContratoMae: Number(e.target.value) })
                            }
                          >
                            <option value={0}>Não</option>
                            <option value={1}>Sim</option>
                          </NativeSelect>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Gera contrato operação</label>
                        <Paper className={pv.medium} elevation={0}>
                          <NativeSelect
                            className={pv.input}
                            disabled={somenteLeitura}
                            value={Number(formValues.geraContratoOperacao ?? 0)}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                geraContratoOperacao: Number(e.target.value),
                              })
                            }
                          >
                            <option value={0}>Não</option>
                            <option value={1}>Sim</option>
                          </NativeSelect>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Assinatura Clicksign</label>
                        <Paper className={pv.medium} elevation={0}>
                          <NativeSelect
                            className={pv.input}
                            disabled={somenteLeitura}
                            value={Number(formValues.assinaturaClickSign ?? 0)}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                assinaturaClickSign: Number(e.target.value),
                              })
                            }
                          >
                            <option value={0}>Não</option>
                            <option value={1}>Sim</option>
                          </NativeSelect>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Clicksign permite recusa</label>
                        <Paper className={pv.medium} elevation={0}>
                          <NativeSelect
                            className={pv.input}
                            disabled={somenteLeitura}
                            value={Number(formValues.clicksignPermiteRecusaAssinatura ?? 0)}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                clicksignPermiteRecusaAssinatura: Number(e.target.value),
                              })
                            }
                          >
                            <option value={0}>Não</option>
                            <option value={1}>Sim</option>
                          </NativeSelect>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Hora inicial envio (seg-sex) HH:mm</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.clicksignHoraInicialEnvioSegundaASexta || ''}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                clicksignHoraInicialEnvioSegundaASexta: e.target.value,
                              })
                            }
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Hora final envio (seg-qui) HH:mm</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.clicksignHoraFinalEnvioSegundaAQuinta || ''}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                clicksignHoraFinalEnvioSegundaAQuinta: e.target.value,
                              })
                            }
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Hora final envio (sexta) HH:mm</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.clicksignHoraFinalEnvioSexta || ''}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                clicksignHoraFinalEnvioSexta: e.target.value,
                              })
                            }
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Hora assinatura (seg-qui) HH:mm</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.clicksignHoraAssinaturaSegundaAQuinta || ''}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                clicksignHoraAssinaturaSegundaAQuinta: e.target.value,
                              })
                            }
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Hora assinatura (sexta) HH:mm</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly={somenteLeitura}
                            value={formValues.clicksignHoraAssinaturaSexta || ''}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                clicksignHoraAssinaturaSexta: e.target.value,
                              })
                            }
                          />
                        </Paper>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded className={pv.accordionRoot}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={pv.summary}>
                    <Typography className={pv.heading}>Configurações de Remessa</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={pv.details}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <label className={pv.label}>Habilita envio de remessa</label>
                        <Paper className={pv.medium} elevation={0}>
                          <NativeSelect
                            className={pv.input}
                            disabled={somenteLeitura}
                            value={Number(formValues.habilitaEnvioRemessa ?? 0)}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                habilitaEnvioRemessa: Number(e.target.value),
                              })
                            }
                          >
                            <option value={0}>Não</option>
                            <option value={1}>Sim</option>
                          </NativeSelect>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Situação de aprovação</label>
                        <Paper className={pv.medium} elevation={0}>
                          <NativeSelect
                            className={pv.input}
                            disabled={somenteLeitura}
                            value={formValues.situacaoAprovacao ?? ''}
                            onChange={(e) =>
                              setFormValues({
                                ...formValues,
                                situacaoAprovacao:
                                  e.target.value === '' ? '' : Number(e.target.value),
                              })
                            }
                          >
                            <option value="">Não definida</option>
                            {SITUACAO_APROVACAO_OPCOES.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </NativeSelect>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <label className={pv.label}>Bancos remessas habilitados</label>
                        {Number(formValues.habilitaEnvioRemessa) !== 1 && (
                          <Paper className={pv.medium} elevation={0} style={{ padding: '10px 14px', background: '#FFF6E5', borderLeft: '4px solid #FB8832', color: '#8A5A00', fontSize: 13 }}>
                            <strong>Atenção:</strong> "Habilita envio de remessa" está como <em>Não</em>. Os bancos selecionados aqui só serão usados quando o flag for ligado nos dados gerais.
                          </Paper>
                        )}
                        {brLoading ? (
                          <Paper className={pv.medium} elevation={0} style={{ padding: '12px', color: '#777' }}>
                            Carregando bancos…
                          </Paper>
                        ) : bancosRemessasDisponiveis.length === 0 ? (
                          <Paper className={pv.medium} elevation={0} style={{ padding: '12px', color: '#777' }}>
                            Nenhum banco de remessa ativo cadastrado.&nbsp;
                            <a href="/app/bancos-remessas/cadastrar" style={{ color: '#026894', fontWeight: 'bold' }}>
                              Cadastrar banco
                            </a>
                          </Paper>
                        ) : (
                          <Paper className={pv.medium} elevation={0} style={{ padding: '8px 12px', flexDirection: 'column', alignItems: 'stretch' }}>
                            <Grid container spacing={1}>
                              {bancosRemessasDisponiveis.map((b) => {
                                const checked = bancosRemessasSelecionados.includes(b.bancoRemessaId);
                                const tipo = (b.tipoPagamento === 1 || b.tipoPagamento === 'TED') ? 'TED' : 'PIX';
                                return (
                                  <Grid item xs={12} sm={6} md={4} key={b.bancoRemessaId}>
                                    <label
                                      htmlFor={`br-${b.bancoRemessaId}`}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 10px',
                                        background: checked ? '#E8F2F8' : '#FAFAFA',
                                        border: `1px solid ${checked ? '#026894' : '#E0E0E0'}`,
                                        borderRadius: 4,
                                        cursor: somenteLeitura ? 'default' : 'pointer',
                                        fontSize: 13,
                                        color: '#333',
                                        transition: 'background 0.15s, border-color 0.15s',
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        id={`br-${b.bancoRemessaId}`}
                                        checked={checked}
                                        disabled={somenteLeitura || brSaving}
                                        onChange={() => toggleBancoRemessa(b.bancoRemessaId)}
                                        style={{ marginRight: 10, accentColor: '#026894' }}
                                      />
                                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <span style={{ fontWeight: 'bold' }}>{b.nome}</span>
                                        <span style={{ fontSize: 11, color: '#777' }}>
                                          Banco {b.bancoId} — Ag {b.agencia} / Cc {b.conta}-{b.dac} — {tipo}
                                        </span>
                                      </div>
                                    </label>
                                  </Grid>
                                );
                              })}
                            </Grid>
                            <div style={{ marginTop: 8, fontSize: 12, color: '#777' }}>
                              {bancosRemessasSelecionados.length} de {bancosRemessasDisponiveis.length} selecionado{bancosRemessasSelecionados.length === 1 ? '' : 's'}
                            </div>
                          </Paper>
                        )}
                      </Grid>
                      {!somenteLeitura && (
                        <Grid item xs={12}>
                          <div className={pv.registerBtn}>
                            <button
                              type="button"
                              className={pv.button}
                              disabled={brSaving || brLoading}
                              onClick={salvarConfiguracoesRemessa}
                              style={{ width: 'auto', padding: '0 20px', whiteSpace: 'nowrap' }}
                            >
                              {brSaving ? 'Salvando…' : 'Salvar configurações de remessa'}
                            </button>
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion className={pv.accordionRoot}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={pv.summary}>
                    <Typography className={pv.heading}>Dados do registro</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={pv.details}>
                    <Grid container>
                      <Grid item xs={6}>
                        <label className={pv.label}>Data de registro</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly
                            value={formatDateTimeBr(formValues.dataRegistro)}
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <label className={pv.label}>Data da atualização</label>
                        <Paper className={pv.medium} elevation={0}>
                          <Input
                            className={pv.input}
                            readOnly
                            value={formatDateTimeBr(formValues.dataAtualizacao)}
                          />
                        </Paper>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </>
            )}
          </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DestinosPagamentos;
